import { useEffect, useMemo, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, parseISO } from 'date-fns';
import { Building2 } from 'lucide-react';
import type { Entity, EntityCreateRequest, EntityUpdateRequest } from '@cortex/domain';

import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Separator } from '@/components/ui/shadcn/separator';
import { FormField } from '@/components/ui/FormField';
import { EmptyState } from '@/components/ui/EmptyState';
import { StatusBadge, toneForLifecycleStatus } from '@/components/ui/StatusBadge';

/**
 * Detail pane for the Entities tab — handles both create and edit modes.
 *
 * Form state and validation run through React Hook Form + Zod
 * (`zodResolver`). The form Zod schema validates the raw text inputs for
 * inline UX; on submit the values are mapped to the canonical
 * `EntityCreateRequest` / `EntityUpdateRequest` domain types (which the
 * API client validates again on the wire). Save is disabled in edit mode
 * until at least one field actually changes, so we never fire an empty
 * PATCH.
 */

export interface EntityTypeOption {
  readonly slug: string;
  readonly displayName: string;
}

export interface EntityDetailProps {
  readonly mode: 'idle' | 'create' | 'edit';
  readonly entity: Entity | null;
  readonly availableTypes: readonly EntityTypeOption[];
  readonly isSaving: boolean;
  readonly errorMessage?: string | null;
  readonly onCreate: (input: EntityCreateRequest) => Promise<void>;
  readonly onUpdate: (id: string, input: EntityUpdateRequest) => Promise<void>;
  readonly onCancel: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;

/**
 * Form-shaped schema: every field is a text-input string. This drives
 * inline validation only; the wire contract lives in `@cortex/domain`.
 */
const EntityFormSchema = z.object({
  entityTypeSlug: z.string().min(1, 'Entity type is required.'),
  name: z.string().trim().min(1, 'Legal name is required.'),
  tradingName: z.string(),
  fspNumber: z.string(),
  registrationNumber: z.string(),
  vatNumber: z.string(),
  primaryContactName: z.string(),
  primaryContactEmail: z
    .string()
    .refine((v) => v.trim() === '' || EMAIL_RE.test(v.trim()), 'Enter a valid email address.'),
  phoneNumber: z.string(),
  addressLine1: z.string(),
  addressLine2: z.string(),
  city: z.string(),
  postalCode: z.string(),
  country: z
    .string()
    .refine(
      (v) => v.trim() === '' || /^[A-Z]{2}$/u.test(v.trim()),
      'Use a two-letter country code (e.g. ZA).',
    ),
  status: z.enum(['Active', 'Suspended']),
});

type EntityFormValues = z.infer<typeof EntityFormSchema>;

const emptyForm = (typeSlug: string): EntityFormValues => ({
  entityTypeSlug: typeSlug,
  name: '',
  tradingName: '',
  fspNumber: '',
  registrationNumber: '',
  vatNumber: '',
  primaryContactName: '',
  primaryContactEmail: '',
  phoneNumber: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postalCode: '',
  country: 'ZA',
  status: 'Active',
});

const fromEntity = (entity: Entity): EntityFormValues => ({
  entityTypeSlug: entity.entityTypeSlug,
  name: entity.name,
  tradingName: entity.tradingName ?? '',
  fspNumber: entity.fspNumber ?? '',
  registrationNumber: entity.registrationNumber ?? '',
  vatNumber: entity.vatNumber ?? '',
  primaryContactName: entity.primaryContactName ?? '',
  primaryContactEmail: entity.primaryContactEmail ?? '',
  phoneNumber: entity.phoneNumber ?? '',
  addressLine1: entity.addressLine1 ?? '',
  addressLine2: entity.addressLine2 ?? '',
  city: entity.city ?? '',
  postalCode: entity.postalCode ?? '',
  country: entity.country,
  status: entity.status,
});

const clean = (s: string): string => s.trim();
const cleanOrNull = (s: string): string | null => (s.trim().length > 0 ? s.trim() : null);

function toCreateRequest(v: EntityFormValues): EntityCreateRequest {
  return {
    entityTypeSlug: v.entityTypeSlug,
    name: clean(v.name),
    ...(clean(v.tradingName) ? { tradingName: clean(v.tradingName) } : {}),
    ...(clean(v.fspNumber) ? { fspNumber: clean(v.fspNumber) } : {}),
    ...(clean(v.registrationNumber) ? { registrationNumber: clean(v.registrationNumber) } : {}),
    ...(clean(v.vatNumber) ? { vatNumber: clean(v.vatNumber) } : {}),
    ...(clean(v.primaryContactName) ? { primaryContactName: clean(v.primaryContactName) } : {}),
    ...(clean(v.primaryContactEmail) ? { primaryContactEmail: clean(v.primaryContactEmail) } : {}),
    ...(clean(v.phoneNumber) ? { phoneNumber: clean(v.phoneNumber) } : {}),
    ...(clean(v.addressLine1) ? { addressLine1: clean(v.addressLine1) } : {}),
    ...(clean(v.addressLine2) ? { addressLine2: clean(v.addressLine2) } : {}),
    ...(clean(v.city) ? { city: clean(v.city) } : {}),
    ...(clean(v.postalCode) ? { postalCode: clean(v.postalCode) } : {}),
    ...(clean(v.country) ? { country: clean(v.country) } : {}),
    status: v.status,
  };
}

function toUpdateRequest(v: EntityFormValues, entity: Entity): EntityUpdateRequest {
  const patch: Record<string, unknown> = {};
  if (clean(v.name) !== entity.name) patch['name'] = clean(v.name);
  if (cleanOrNull(v.tradingName) !== entity.tradingName)
    patch['tradingName'] = cleanOrNull(v.tradingName);
  if (cleanOrNull(v.fspNumber) !== entity.fspNumber) patch['fspNumber'] = cleanOrNull(v.fspNumber);
  if (cleanOrNull(v.registrationNumber) !== entity.registrationNumber)
    patch['registrationNumber'] = cleanOrNull(v.registrationNumber);
  if (cleanOrNull(v.vatNumber) !== entity.vatNumber) patch['vatNumber'] = cleanOrNull(v.vatNumber);
  if (cleanOrNull(v.primaryContactName) !== entity.primaryContactName)
    patch['primaryContactName'] = cleanOrNull(v.primaryContactName);
  if (cleanOrNull(v.primaryContactEmail) !== entity.primaryContactEmail)
    patch['primaryContactEmail'] = cleanOrNull(v.primaryContactEmail);
  if (cleanOrNull(v.phoneNumber) !== entity.phoneNumber)
    patch['phoneNumber'] = cleanOrNull(v.phoneNumber);
  if (cleanOrNull(v.addressLine1) !== entity.addressLine1)
    patch['addressLine1'] = cleanOrNull(v.addressLine1);
  if (cleanOrNull(v.addressLine2) !== entity.addressLine2)
    patch['addressLine2'] = cleanOrNull(v.addressLine2);
  if (cleanOrNull(v.city) !== entity.city) patch['city'] = cleanOrNull(v.city);
  if (cleanOrNull(v.postalCode) !== entity.postalCode)
    patch['postalCode'] = cleanOrNull(v.postalCode);
  if (clean(v.country) !== entity.country) patch['country'] = clean(v.country);
  if (v.status !== entity.status) patch['status'] = v.status;
  return patch as EntityUpdateRequest;
}

export function EntityDetail({
  mode,
  entity,
  availableTypes,
  isSaving,
  errorMessage,
  onCreate,
  onUpdate,
  onCancel,
}: EntityDetailProps) {
  const defaultTypeSlug = useMemo(
    () => availableTypes.find((t) => t.slug === 'brokerage')?.slug ?? availableTypes[0]?.slug ?? '',
    [availableTypes],
  );

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<EntityFormValues>({
    resolver: zodResolver(EntityFormSchema),
    defaultValues: mode === 'edit' && entity ? fromEntity(entity) : emptyForm(defaultTypeSlug),
  });

  useEffect(() => {
    if (mode === 'edit' && entity) reset(fromEntity(entity));
    else if (mode === 'create') reset(emptyForm(defaultTypeSlug));
  }, [mode, entity, defaultTypeSlug, reset]);

  const values = watch();
  const dirty =
    mode === 'create' || (entity ? Object.keys(toUpdateRequest(values, entity)).length > 0 : false);

  const onSubmit = handleSubmit(async (data) => {
    if (mode === 'create') await onCreate(toCreateRequest(data));
    else if (entity) await onUpdate(entity.id, toUpdateRequest(data, entity));
  });

  if (mode === 'idle') {
    return (
      <div className="flex flex-1 items-center justify-center">
        <EmptyState
          icon={Building2}
          title="Select an entity to view details."
          description="Or click “New entity” on the left to add one."
        />
      </div>
    );
  }

  const countryField = register('country');

  return (
    // `noValidate`: React Hook Form + Zod own validation so messages are
    // consistent and styled. Native browser popups would otherwise
    // pre-empt the submit (e.g. on an invalid type="email" value) before
    // our validation runs.
    <form onSubmit={onSubmit} noValidate className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-start justify-between border-b border-border bg-card px-5 py-4">
        <div>
          <h2 className="text-sm font-bold text-foreground">
            {mode === 'create' ? 'New entity' : entity?.name}
          </h2>
          {mode === 'edit' && entity && (
            <div className="mt-1 flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{entity.entityTypeDisplayName}</span>
              <span className="text-xs text-border">·</span>
              <StatusBadge tone={toneForLifecycleStatus(entity.status)} label={entity.status} />
            </div>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
        {errorMessage && (
          <div
            className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            role="alert"
          >
            {errorMessage}
          </div>
        )}

        <FieldSection title="Identification">
          {mode === 'create' && (
            <FormField label="Entity type" required error={errors.entityTypeSlug?.message}>
              <select className={SELECT_CLASS} disabled={isSaving} {...register('entityTypeSlug')}>
                {availableTypes.map((t) => (
                  <option key={t.slug} value={t.slug}>
                    {t.displayName}
                  </option>
                ))}
              </select>
            </FormField>
          )}
          <FormField label="Legal name" required error={errors.name?.message}>
            <Input type="text" disabled={isSaving} {...register('name')} />
          </FormField>
          <FormField label="Trading name" hint="Often shown on policy schedules.">
            <Input type="text" disabled={isSaving} {...register('tradingName')} />
          </FormField>
        </FieldSection>

        <Separator />

        <FieldSection title="Regulatory">
          <FieldRow>
            <FormField label="FSP number" hint="South African FSP number.">
              <Input
                type="text"
                className="font-mono"
                disabled={isSaving}
                {...register('fspNumber')}
              />
            </FormField>
            <FormField label="Company registration number">
              <Input
                type="text"
                className="font-mono"
                disabled={isSaving}
                {...register('registrationNumber')}
              />
            </FormField>
          </FieldRow>
          <FormField label="VAT number">
            <Input
              type="text"
              className="font-mono"
              disabled={isSaving}
              {...register('vatNumber')}
            />
          </FormField>
        </FieldSection>

        <Separator />

        <FieldSection title="Primary contact">
          <FormField label="Contact name">
            <Input type="text" disabled={isSaving} {...register('primaryContactName')} />
          </FormField>
          <FieldRow>
            <FormField label="Contact email" error={errors.primaryContactEmail?.message}>
              <Input type="email" disabled={isSaving} {...register('primaryContactEmail')} />
            </FormField>
            <FormField label="Phone number">
              <Input type="tel" disabled={isSaving} {...register('phoneNumber')} />
            </FormField>
          </FieldRow>
        </FieldSection>

        <Separator />

        <FieldSection title="Address">
          <FormField label="Address line 1">
            <Input type="text" disabled={isSaving} {...register('addressLine1')} />
          </FormField>
          <FormField label="Address line 2">
            <Input type="text" disabled={isSaving} {...register('addressLine2')} />
          </FormField>
          <FieldRow>
            <FormField label="City">
              <Input type="text" disabled={isSaving} {...register('city')} />
            </FormField>
            <FormField label="Postal code">
              <Input
                type="text"
                className="font-mono"
                disabled={isSaving}
                {...register('postalCode')}
              />
            </FormField>
            <FormField label="Country" hint="ISO 2-letter code." error={errors.country?.message}>
              <Input
                type="text"
                maxLength={2}
                className="font-mono uppercase"
                disabled={isSaving}
                {...countryField}
                onChange={(e) => {
                  e.currentTarget.value = e.currentTarget.value.toUpperCase();
                  void countryField.onChange(e);
                }}
              />
            </FormField>
          </FieldRow>
        </FieldSection>

        <Separator />

        <FieldSection title="Lifecycle">
          <FormField label="Status">
            <select className={SELECT_CLASS} disabled={isSaving} {...register('status')}>
              <option value="Active">Active</option>
              <option value="Suspended">Suspended</option>
            </select>
          </FormField>
        </FieldSection>

        {mode === 'edit' && entity && (
          <p className="border-t border-border pt-3 text-xs text-muted-foreground">
            Created {formatTimestamp(entity.createdAt)} · Last updated{' '}
            {formatTimestamp(entity.updatedAt)}
          </p>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-border bg-muted/40 px-5 py-3">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={isSaving || !dirty}>
          {isSaving ? 'Saving…' : mode === 'create' ? 'Create entity' : 'Save changes'}
        </Button>
      </div>
    </form>
  );
}

const SELECT_CLASS =
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs transition-colors focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50';

function FieldSection({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}</h3>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function FieldRow({ children }: { readonly children: ReactNode }) {
  return <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">{children}</div>;
}

const formatTimestamp = (iso: string): string => {
  try {
    return format(parseISO(iso), 'd MMM yyyy, HH:mm');
  } catch {
    return iso;
  }
};
